import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
// import StarIcon from '@mui/icons-material/Star';
import {
  Box,
  Button,
  DialogContent,
  Grid,
  Stack,
  TextField,
  Tooltip,
  // List,
  // ListItem,
  // ListItemText,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import type { Resolver } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import type { Popup2290ContactUS } from '@/types/excise/Popup2290ContactUS';

import CustomTextMask from './CustomTextMask';
import Loader from './Loader';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(0),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
  isForm2290BulkUploadTemplate?: boolean;
  setIsForm2290BulkUploadTemplate?: (value: boolean) => void;
}
export interface Form2290ContactUsProps {
  isForm2290BulkUploadTemplate: boolean;
  setIsForm2290BulkUploadTemplate: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  setSnackOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 0 }} {...other}>
      {children}
      {onClose ? (
        <Tooltip title="Close" arrow>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              p: 0,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      ) : null}
    </DialogTitle>
  );
}

const PopupContactUs = (props: Form2290ContactUsProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const validationSchema = Yup.object().shape({
    Name: Yup.string().required('Name is required'),
    Email: Yup.string()
      .required('Email Address is required')
      .email('Enter valid Email Address'),
    Phone: Yup.string()
      .required('Phone Number is required')
      .matches(
        /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/,
        'Please enter a valid phone number'
      ),
    BusinessName: Yup.string().required('Business/Company is required'),
    Message: Yup.string().required('Comments is required'),
  });

  const defaultValue: Popup2290ContactUS = {
    Email: '',
    Name: '',
    Phone: '',
    BusinessName: '',
    Message: '',
    Token: '',
  };

  const { handleSubmit, control, reset } = useForm<Popup2290ContactUS>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema) as unknown as Resolver<
      Popup2290ContactUS,
      any
    >,
    defaultValues: defaultValue,
  });

  useEffect(() => {
    const loadScriptByURL = (id: any, url: any, callback: any) => {
      const isScriptExist = document.getElementById(id);

      if (!isScriptExist) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;
        script.id = id;
        // eslint-disable-next-line func-names
        script.onload = function () {
          if (callback) callback();
        };
        document.body.appendChild(script);
      }

      if (isScriptExist && callback) callback();
    };

    // load the script by passing the URL
    loadScriptByURL(
      'recaptcha-key',
      `https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}&badge=bottomleft`,
      // eslint-disable-next-line func-names
      function () {
        // console.log('Script loaded!');
      }
    );
  }, []);

  const onSubmit = async (data: Popup2290ContactUS) => {
    setIsLoading(true);
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY, {
          action: 'submit',
        })
        .then(async (token: any) => {
          /* send data to the server */
          const postData: Popup2290ContactUS = data;
          postData.Token = token;

          const address = `/api/contactus/`;
          const contactusResponse = await fetch(address, {
            method: 'POST',
            body: JSON.stringify(postData),
          });
          const contactusResponseData = await contactusResponse.json();
          if (contactusResponseData?.status === 'Success') {
            reset();
            props.setSnackOpen(true);
            props.setIsForm2290BulkUploadTemplate(false);
          }
          setIsLoading(false);
        })
        .catch(() => {});
    });
    reset();
  };

  const handleClose = () => {
    props.setIsForm2290BulkUploadTemplate(false);
  };

  useEffect(() => {
    reset(defaultValue);
  }, [props.isForm2290BulkUploadTemplate]);

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={props.isForm2290BulkUploadTemplate}
      className="holiday-theme"
      fullWidth
      maxWidth="md"
    >
      <Loader isLoading={isLoading} />
      <BootstrapDialogTitle
        id="customized-dialog-title"
        onClose={handleClose}
      />
      <DialogContent>
        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          justifyContent="space-between"
          alignItems="stretch"
          spacing={0}
        >
          <Box
            component="form"
            autoComplete="off"
            onSubmit={handleSubmit(onSubmit)}
            sx={{
              width: '100%',
              p: 3,
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 600,
                color: '#000000',
                fontSize: { md: 28, sm: 20, xs: 18 },
                mb: 2,
                textAlign: 'center',
              }}
            >
              Managing{' '}
              <span style={{ fontWeight: 700, color: '#1da94a' }}>
                Multiple Clients
              </span>{' '}
              or fleets?
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: 17, fontWeight: 500, mb: 2, textAlign: 'center' }}
            >
              Fill in your details below and our team will contact you with the
              best bulk pricing plan for your
              <br /> Form 2290 filings.
            </Typography>
            <Grid container spacing={1.2}>
              <Grid size={12}>
                {' '}
                <Controller
                  name="Name"
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <TextField
                      id="Name"
                      name="UserName"
                      label="*Full Name"
                      color="secondary"
                      variant="outlined"
                      className={value ? 'prefilled' : 'mandatory'}
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error ? error.message : null}
                      inputProps={{
                        maxLength: 100,
                        maxHight: 100,
                      }}
                      fullWidth
                      sx={{ mb: 0, py: 0, px: 0 }}
                    />
                  )}
                />
              </Grid>
              <Grid size={12}>
                {' '}
                <Controller
                  name="Email"
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <TextField
                      className={value ? 'prefilled' : 'mandatory'}
                      id="Email"
                      label="*Email Address"
                      variant="outlined"
                      autoComplete="nope"
                      color="secondary"
                      inputProps={{
                        maxLength: 100,
                      }}
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error ? error.message : null}
                      fullWidth
                      sx={{ mb: 0 }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                {' '}
                <Controller
                  name="Phone"
                  control={control}
                  defaultValue=""
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <TextField
                      id="Phone"
                      label="*Phone Number"
                      className={value ? 'prefilled' : 'mandatory'}
                      variant="outlined"
                      color="secondary"
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error ? error.message : null}
                      InputProps={{
                        inputComponent: CustomTextMask as any,
                        inputProps: {
                          mask: '(#00) 000-0000',
                          maxLength: 14,
                        },
                      }}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                {' '}
                <Controller
                  name="BusinessName"
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <TextField
                      className={value ? 'prefilled' : 'mandatory'}
                      id="BusinessCompanyName:"
                      label="*Business / Company Name"
                      variant="outlined"
                      autoComplete="nope"
                      color="secondary"
                      inputProps={{
                        maxLength: 100,
                      }}
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error ? error.message : null}
                      fullWidth
                      sx={{ mb: 0 }}
                    />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <Controller
                  name="Message"
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <TextField
                      className={value ? 'prefilled' : 'mandatory'}
                      id="Comments"
                      label="*Comments"
                      placeholder="Enter any notes, special instructions, or requirements for our team. "
                      variant="outlined"
                      autoComplete="nope"
                      color="secondary"
                      inputProps={{
                        maxLength: 200,
                      }}
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error ? error.message : null}
                      fullWidth
                      multiline
                      rows={3}
                      sx={{ mb: 2 }}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Box sx={{ textAlign: 'center', justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                type="submit"
                sx={{
                  textTransform: 'none',
                  boxShadow: 'none',
                  borderRadius: 4,
                  display: 'inline-block',
                  fontSize: { xs: 18, sm: 20, md: 22, lg: 24 },
                }}
              >
                Contact Us
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              maxWidth: 400,
              width: '100%',
              minHeight: { xs: 339, sm: 380, md: '100%' },
              minWidth: { xs: 200, sm: 339, md: 327 },
              display: { xs: 'none', sm: 'block' },
            }}
            className="contact2290-popup"
          />
        </Stack>
      </DialogContent>
    </BootstrapDialog>
  );
};

export default PopupContactUs;
